import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, CalendarDays, Plus, Trash2, Timer, CalendarOff } from 'lucide-react';
import { format } from 'date-fns';
import { enUS, es, fr } from 'date-fns/locale';
import { toast } from 'react-toastify';

import { useLanguage } from '../../contexts/LanguageContext';
import useConfirm from '../../hooks/useConfirm';
import ConfirmationModal from '../modals/ConfirmationModal';
import { appointmentsAPI, handleApiError } from '../../utils/api';
import { parseLocalDate } from '../../utils/dateUtils';

const AvailabilityAndHolidays = ({ showToast }) => {
  const { t, currentLanguage } = useLanguage();
  const { confirm, confirmProps } = useConfirm();

  const notify = useCallback(
    (message, type = 'info') => {
      if (showToast) {
        showToast(message, type);
        return;
      }

      if (type === 'success') toast.success(message);
      else if (type === 'error') toast.error(message);
      else toast.info(message);
    },
    [showToast]
  );

  const [activeSection, setActiveSection] = useState('availability');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [availabilities, setAvailabilities] = useState([]);
  const [availabilityForm, setAvailabilityForm] = useState({
    day_of_week: 1,
    start_time: '09:00',
    end_time: '18:00',
    is_active: true
  });

  const [holidays, setHolidays] = useState([]);
  const [holidayForm, setHolidayForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    name: '',
    is_recurring: false
  });

  const locale = useMemo(() => {
    if (currentLanguage === 'en') return enUS;
    if (currentLanguage === 'es') return es;
    return fr;
  }, [currentLanguage]);

  const daysOfWeek = useMemo(
    () => [
      { value: 1, label: t('days.monday', 'Monday') },
      { value: 2, label: t('days.tuesday', 'Tuesday') },
      { value: 3, label: t('days.wednesday', 'Wednesday') },
      { value: 4, label: t('days.thursday', 'Thursday') },
      { value: 5, label: t('days.friday', 'Friday') },
      { value: 6, label: t('days.saturday', 'Saturday') },
      { value: 0, label: t('days.sunday', 'Sunday') }
    ],
    [t]
  );

  const groupedAvailabilities = useMemo(() => {
    const groups = new Map(daysOfWeek.map((day) => [day.value, []]));

    availabilities.forEach((item) => {
      const list = groups.get(item.day_of_week) || [];
      list.push(item);
      groups.set(item.day_of_week, list);
    });

    daysOfWeek.forEach((day) => {
      const list = groups.get(day.value) || [];
      list.sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));
      groups.set(day.value, list);
    });

    return groups;
  }, [availabilities, daysOfWeek]);

  const sortedHolidays = useMemo(
    () =>
      [...holidays].sort((a, b) => String(a.date || '').localeCompare(String(b.date || ''))),
    [holidays]
  );

  const fetchAvailabilities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await appointmentsAPI.getAvailabilities();
      setAvailabilities(response.data || []);
    } catch (error) {
      notify(
        handleApiError(
          error,
          t('appointments.availability.error.fetch', 'Unable to load availabilities')
        ),
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const response = await appointmentsAPI.getHolidays();
      setHolidays(response.data || []);
    } catch (error) {
      notify(
        handleApiError(error, t('appointments.holidays.error.fetch', 'Unable to load holidays')),
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    if (activeSection === 'availability') {
      fetchAvailabilities();
    } else {
      fetchHolidays();
    }
  }, [activeSection, fetchAvailabilities, fetchHolidays]);

  const handleAddAvailability = async () => {
    try {
      setSaving(true);
      const response = await appointmentsAPI.createAvailability(availabilityForm);
      setAvailabilities((prev) => [...prev, response.data]);
      setAvailabilityForm({
        day_of_week: 1,
        start_time: '09:00',
        end_time: '18:00',
        is_active: true
      });
      notify(t('appointments.availability.success.add', 'Availability added successfully'), 'success');
    } catch (error) {
      notify(
        handleApiError(error, t('appointments.availability.error.add', 'Unable to add availability')),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAvailability = async (id) => {
    const shouldDelete = await confirm({
      title: t('appointments.availability.confirmDeleteTitle', 'Delete availability'),
      message: t(
        'appointments.availability.confirmDelete',
        'Do you really want to delete this availability slot?'
      ),
      type: 'danger',
      confirmText: t('common.delete', 'Delete'),
      cancelText: t('common.cancel', 'Cancel')
    });

    if (!shouldDelete) return;

    try {
      await appointmentsAPI.deleteAvailability(id);
      setAvailabilities((prev) => prev.filter((item) => item.id !== id));
      notify(
        t('appointments.availability.success.delete', 'Availability deleted successfully'),
        'success'
      );
    } catch (error) {
      notify(
        handleApiError(
          error,
          t('appointments.availability.error.delete', 'Unable to delete availability')
        ),
        'error'
      );
    }
  };

  const handleAddHoliday = async () => {
    if (!holidayForm.name.trim()) {
      notify(t('appointments.holidays.error.nameRequired', 'Holiday name is required'), 'error');
      return;
    }

    try {
      setSaving(true);
      const response = await appointmentsAPI.createHoliday(holidayForm);
      setHolidays((prev) => [...prev, response.data]);
      setHolidayForm({
        date: format(new Date(), 'yyyy-MM-dd'),
        name: '',
        is_recurring: false
      });
      notify(t('appointments.holidays.success.add', 'Holiday added successfully'), 'success');
    } catch (error) {
      notify(
        handleApiError(error, t('appointments.holidays.error.add', 'Unable to add holiday')),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHoliday = async (id) => {
    const shouldDelete = await confirm({
      title: t('appointments.holidays.confirmDeleteTitle', 'Delete holiday'),
      message: t('appointments.holidays.confirmDelete', 'Do you really want to delete this holiday?'),
      type: 'danger',
      confirmText: t('common.delete', 'Delete'),
      cancelText: t('common.cancel', 'Cancel')
    });

    if (!shouldDelete) return;

    try {
      await appointmentsAPI.deleteHoliday(id);
      setHolidays((prev) => prev.filter((item) => item.id !== id));
      notify(t('appointments.holidays.success.delete', 'Holiday deleted successfully'), 'success');
    } catch (error) {
      notify(
        handleApiError(error, t('appointments.holidays.error.delete', 'Unable to delete holiday')),
        'error'
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-2 inline-flex gap-2">
        <button
          onClick={() => setActiveSection('availability')}
          className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeSection === 'availability'
              ? 'bg-indigo-50 text-indigo-700'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Clock className="h-4 w-4 mr-2" />
          {t('appointments.availability.title', 'My Availability')}
        </button>
        <button
          onClick={() => setActiveSection('holidays')}
          className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeSection === 'holidays'
              ? 'bg-indigo-50 text-indigo-700'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <CalendarDays className="h-4 w-4 mr-2" />
          {t('appointments.holidays.title', 'Holidays')}
        </button>
      </div>

      <AnimatePresence mode="wait">
        {activeSection === 'availability' ? (
          <motion.section
            key="availability"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="space-y-4"
          >
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Timer className="h-5 w-5 text-indigo-600" />
                {t('appointments.availability.title', 'My Availability')}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {t(
                  'appointments.availability.description',
                  'Configure your availability slots for each day of the week'
                )}
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 inline-flex items-center">
                <Plus className="h-4 w-4 mr-2 text-indigo-600" />
                {t('appointments.availability.add', 'Add availability')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <select
                  value={availabilityForm.day_of_week}
                  onChange={(e) =>
                    setAvailabilityForm((prev) => ({
                      ...prev,
                      day_of_week: parseInt(e.target.value, 10)
                    }))
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  {daysOfWeek.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
                <input
                  type="time"
                  value={availabilityForm.start_time}
                  onChange={(e) =>
                    setAvailabilityForm((prev) => ({ ...prev, start_time: e.target.value }))
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
                <input
                  type="time"
                  value={availabilityForm.end_time}
                  onChange={(e) =>
                    setAvailabilityForm((prev) => ({ ...prev, end_time: e.target.value }))
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
                <button
                  onClick={handleAddAvailability}
                  disabled={saving}
                  className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('common.add', 'Add')}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              {loading ? (
                <div className="py-10 text-center text-gray-500">
                  {t('common.loading', 'Loading...')}
                </div>
              ) : (
                <div className="space-y-4">
                  {daysOfWeek.map((day) => {
                    const daySlots = groupedAvailabilities.get(day.value) || [];
                    return (
                      <div key={day.value} className="rounded-lg border border-gray-100 p-3">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">{day.label}</h4>
                        {daySlots.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            {t('appointments.availability.noneForDay', 'No availability')}
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {daySlots.map((slot) => (
                              <div
                                key={slot.id}
                                className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2"
                              >
                                <Clock className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium text-gray-800">
                                  {String(slot.start_time).slice(0, 5)} -{' '}
                                  {String(slot.end_time).slice(0, 5)}
                                </span>
                                {!slot.is_active && (
                                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                                    {t('common.inactive', 'Inactive')}
                                  </span>
                                )}
                                <button
                                  onClick={() => handleDeleteAvailability(slot.id)}
                                  className="ml-auto text-red-500 hover:text-red-700"
                                  aria-label={t('common.delete', 'Delete')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.section>
        ) : (
          <motion.section
            key="holidays"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="space-y-4"
          >
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <CalendarOff className="h-5 w-5 text-indigo-600" />
                {t('appointments.holidays.title', 'Holidays and Closures')}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {t(
                  'appointments.holidays.description',
                  'Define days where you are not available for booking'
                )}
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 inline-flex items-center">
                <Plus className="h-4 w-4 mr-2 text-indigo-600" />
                {t('appointments.holidays.add', 'Add holiday')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input
                  type="date"
                  value={holidayForm.date}
                  onChange={(e) =>
                    setHolidayForm((prev) => ({ ...prev, date: e.target.value }))
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  value={holidayForm.name}
                  onChange={(e) =>
                    setHolidayForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder={t('appointments.holidays.namePlaceholder', 'Holiday name')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm md:col-span-2"
                />
                <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
                  <label className="text-sm text-gray-700 inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={holidayForm.is_recurring}
                      onChange={(e) =>
                        setHolidayForm((prev) => ({
                          ...prev,
                          is_recurring: e.target.checked
                        }))
                      }
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                    />
                    <span className="ml-2">{t('appointments.holidays.recurring', 'Recurring')}</span>
                  </label>
                  <button
                    onClick={handleAddHoliday}
                    disabled={saving}
                    className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              {loading ? (
                <div className="py-10 text-center text-gray-500">
                  {t('common.loading', 'Loading...')}
                </div>
              ) : sortedHolidays.length === 0 ? (
                <div className="py-10 text-center text-gray-500">
                  {t('appointments.holidays.none', 'No holidays defined')}
                </div>
              ) : (
                <div className="space-y-2">
                  {sortedHolidays.map((holiday) => (
                    <div
                      key={holiday.id}
                      className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2"
                    >
                      <div className="inline-flex items-center min-w-0">
                        <CalendarDays className="h-4 w-4 text-red-500 mr-2" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{holiday.name}</p>
                          <p className="text-xs text-gray-600">
                            {holiday.date && parseLocalDate(holiday.date)
                              ? format(parseLocalDate(holiday.date), 'EEEE d MMMM yyyy', { locale })
                              : t('appointments.holidays.invalidDate', 'Invalid date')}
                          </p>
                        </div>
                        {holiday.is_recurring && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                            {t('appointments.holidays.recurring', 'Recurring')}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteHoliday(holiday.id)}
                        className="text-red-500 hover:text-red-700 ml-3"
                        aria-label={t('common.delete', 'Delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      <ConfirmationModal {...confirmProps} />
    </div>
  );
};

export default AvailabilityAndHolidays;
